import React from 'react';

import { ChevronIcon } from '@/assets/icons/chevron';
import { ChevronFullIcon } from '@/assets/icons/chevron-full';
import { DeleteXIcon } from '@/assets/icons/delete-x';
import DeleteXDesktopBoldIcon from '@/assets/icons/delete-x-desktop-bold';
import color from '@/core/features/styles/color';

export type ChipSize = 'L' | 'S' | 'XL' | 'XXL' | 'normal';

export type ChipProps = React.PropsWithChildren<{
    active?: boolean;
    closeButtonSize?: number;
    closeable?: boolean;
    disabled?: boolean;
    extendable?: boolean;
    largeIcons?: boolean;
    loading?: boolean;
    onClick: () => void;
    onDeselect?: () => void;
    qaId?: string;
    rounded?: boolean;
    shadow?: boolean;
    size?: ChipSize;
}>;

export default function Chip(props: ChipProps) {
    const {
        active,
        children,
        closeButtonSize = 12,
        closeable,
        disabled,
        extendable,
        largeIcons,
        loading,
        onClick,
        onDeselect,
        qaId,
        rounded,
        shadow,
        size = 'normal',
    } = props;

    return (
        <div
            className={`chip bg-white border padding-left-10 padding-right-10 border-alto vertical-middle max-width-100 border-radius-3 pointer flex-center ${size} ${active ? 'active' : ''} ${disabled ? 'disabled' : ''} ${loading ? 'loading' : ''} ${rounded ? 'rounded' : ''} ${shadow ? 'shadow' : ''}`}
            data-qa-id={qaId}
        >
            <div className={'content width-100 flex align-center'}>
                <span
                    className={'text width-100 text-nowrap relative'}
                    onClick={onClick}
                >
                    {children}
                </span>
                {closeable &&
                    onDeselect &&
                    (largeIcons ? (
                        <DeleteXDesktopBoldIcon
                            className={`chipIcon flex-center relative ${closeable ? 'flex' : ''}`}
                            color={color('endeavour')}
                            height={closeButtonSize}
                            onClick={onDeselect}
                            width={closeButtonSize}
                        />
                    ) : (
                        <DeleteXIcon
                            className={`chipIcon flex-center relative ${closeable ? 'border-radius-circle closeIcon' : ''}`}
                            color={color('endeavour')}
                            height={6}
                            onClick={onDeselect}
                            width={6}
                        />
                    ))}
                {extendable &&
                    (largeIcons ? (
                        <ChevronIcon
                            className={`chipIcon flex-center relative ${extendable ? 'flex largeChevronIcon' : ''}`}
                            color={color('endeavour')}
                            height={9}
                            onClick={onClick}
                            width={15}
                        />
                    ) : (
                        <ChevronFullIcon
                            className={`chipIcon flex-center relative ${extendable ? 'flex' : ''}`}
                            color={color('black')}
                            height={10}
                            onClick={onClick}
                            width={7}
                        />
                    ))}
            </div>
            <style jsx>{`
                .chip.normal {
                    height: 32px;
                    font-size: 14px;
                }
                .chip.S {
                    height: 24px;
                    font-size: 12px;
                }
                .chip.L {
                    padding-top: 1px;
                    height: 34px;
                    font-size: 16px;
                }
                .chip.XL {
                    height: 38px;
                    font-size: 18px;
                    padding: 0 10px;
                }
                .chip.XXL {
                    height: 50px;
                    font-size: 18px;
                    padding: 0 15px;
                }
                .chip.shadow {
                    box-shadow: rgba(0, 0, 0, 0.042) 1px 1px 2.6px;
                }
                .chip.rounded {
                    border-radius: 30px;
                    padding: 0 8px;
                }
                .chip.active {
                    background-color: ${color('foam')};
                    border: 1px solid ${color('endeavour')};
                }
                .chip.disabled:not(.active) {
                    background-color: ${color('white')};
                    color: ${color('alto')};
                    cursor: not-allowed;
                }
                :global(body:not(.body--mobile)) .chip:not(.disabled):hover {
                    background-color: ${color('foam')};
                }
                .chip.loading {
                    opacity: 0.6;
                }
                .content {
                    flex-shrink: 1;
                }
                .text::after {
                    content: '';
                    position: absolute;
                    top: -10px;
                    bottom: -10px;
                    left: -10px;
                    right: -10px;
                }
            `}</style>
            {/* Scoped JSX doesn't work on classNames passed to external components */}
            <style
                global
                jsx
            >{`
                .chipIcon {
                    transform: rotate(90deg);
                    margin-left: 8px;
                }
                .chipIcon.closeIcon {
                    width: 16px;
                    height: 16px;
                    margin-top: -2px;
                    margin-bottom: -2px;
                    background-color: ${color('endeavour-01')};
                }
                .chipIcon.largeChevronIcon {
                    transform: rotate(90deg);
                }
                .chipIcon::after {
                    content: '';
                    position: absolute;
                    top: -10px;
                    bottom: -10px;
                    left: -10px;
                    right: -10px;
                }
            `}</style>
        </div>
    );
}
